import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PrintButton from "../common/PrintButton/PrintButton";
import PrintableContent from "../common/PrintableContent/PrintableContent";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useGetReceiptById } from "../../hooks/api/useReceipt";
import { useGetInvoiceById } from "../../hooks/api/useInvoice";
import { useGetOrder } from "../../hooks/api/useSalesOrder";
import { useGetProfile } from "../../hooks/api/useProfile";
import type { Order } from "../Orders/types/salesOrder";
import type { Invoice } from "../Invoice/types/invoice";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  KivuI18nContext,
  PAGE_PORTAL_ORDERS_INVOICE_DETAIL,
  PAGE_PORTAL_ORDERS_PAYMENT_DETAIL,
  PAGE_PORTAL_ORDERS_SALES_DETAIL,
  PORTAL_PREFIX,
  localizedPath,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import NoReceiptDetailFound from "./NoReceiptDetailFound";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import { useReportError } from "../../hooks/useReportError";
import ReceiptDetailPrintableBody from "./ReceiptDetailPrintableBody";

const ReceiptDetail: React.FC = () => {
  const { receiptId } = useParams<{ receiptId: string }>();
  const navigate = useNavigate();
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const { receiptDetail, receiptList } = useSelector(
    (state: RootState) => state.receipts
  );
  const { invoiceDetail } = useSelector((state: RootState) => state.invoices);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  const getReceiptById = useGetReceiptById();
  const getProfile = useGetProfile();
  const getInvoiceById = useGetInvoiceById();
  const getOrder = useGetOrder();
  const reportError = useReportError();
  const printContentRef = useRef<HTMLDivElement>(null);
  const [orderForPrint, setOrderForPrint] = useState<Order | null>(null);
  const [linkedInvoice, setLinkedInvoice] = useState<Invoice | null>(null);

  useEffect(() => {
    if (receiptId) {
      (async () => {
        try {
          await getReceiptById.mutateAsync({ receiptId });
        } catch (err) {
          reportError("Failed to fetch receipt detail (UI)", err, {
            component: "ReceiptDetail",
            receiptId: receiptId ?? ""
          });
        }
      })();
    }
  }, [receiptId, reportError]);

  // Ensure profile is in Redux for print cashier (firstName, etc.) when landing here directly.
  useEffect(() => {
    if (!userDetails) {
      void getProfile.mutateAsync().catch(() => {});
    }
  }, [userDetails, getProfile.mutateAsync]);

  // Try receiptDetail first, fall back to receiptList
  const receipt = useMemo(() => {
    if (receiptDetail?.receiptId === receiptId) return receiptDetail;
    return receiptList.find((r) => r.receiptId === receiptId);
  }, [receiptDetail, receiptId, receiptList]);

  // Linked invoice for this receipt — local state so print/summary match partial payments
  // even if Redux `invoiceDetail` is stale or print runs before the store updates.
  useEffect(() => {
    if (!receipt?.invoiceId) {
      setLinkedInvoice(null);
      return;
    }
    const invoiceId = receipt.invoiceId;
    let cancelled = false;
    setLinkedInvoice(null);
    (async () => {
      try {
        const inv = await getInvoiceById.mutateAsync({ invoiceId });
        if (!cancelled && inv?.invoiceId === invoiceId) {
          setLinkedInvoice(inv);
        }
      } catch (err) {
        reportError("Failed to fetch invoice detail from receipt page", err, {
          component: "ReceiptDetail",
          invoiceId
        });
        if (!cancelled) setLinkedInvoice(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [receipt?.invoiceId, getInvoiceById.mutateAsync, reportError]);

  useEffect(() => {
    if (!receipt?.orderId) {
      setOrderForPrint(null);
      return;
    }
    let cancelled = false;
    setOrderForPrint(null);
    (async () => {
      try {
        const order = await getOrder.mutateAsync({ orderId: receipt.orderId });
        if (!cancelled) setOrderForPrint(order);
      } catch (err) {
        reportError("Failed to fetch order for receipt print", err, {
          component: "ReceiptDetail",
          orderId: receipt.orderId
        });
        if (!cancelled) setOrderForPrint(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [receipt?.orderId, getOrder.mutateAsync, reportError]);

  if (getReceiptById.isPending) {
    return (
      <CommonLoader
        loadingMessage={t(
          "receipt-detail.fetching",
          "Fetching receipt details..."
        )}
      />
    );
  }

  if (!receipt || receipt.receiptId !== receiptId) {
    return <NoReceiptDetailFound />;
  }

  const invoiceDetailPath = localizedPath(
    `/${PORTAL_PREFIX}/${PAGE_PORTAL_ORDERS_INVOICE_DETAIL.replace(
      ":invoiceId",
      receipt.invoiceId
    )}`,
    language,
    defaultLang
  );
  const paymentDetailPath = localizedPath(
    `/${PORTAL_PREFIX}/${PAGE_PORTAL_ORDERS_PAYMENT_DETAIL.replace(
      ":paymentId",
      receipt.paymentId
    )}`,
    language,
    defaultLang
  );
  const salesOrderDetailPath = localizedPath(
    `/${PORTAL_PREFIX}/${PAGE_PORTAL_ORDERS_SALES_DETAIL.replace(
      ":orderId",
      receipt.orderId
    )}`,
    language,
    defaultLang
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box
        sx={{
          mb: 2.5,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: { xs: 1, sm: 1.25 },
          "@media print": { display: "none" }
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => navigate(-1)}
          aria-label={t("receipt-detail-back-to-receipts", "Back to Receipts")}
          sx={{
            display: { xs: "inline-flex", md: "none" },
            alignSelf: "flex-start",
            alignItems: "center",
            gap: 0.35,
            py: 0,
            mr: "auto",
            border: "none",
            background: "none",
            cursor: "pointer",
            font: "inherit",
            color: BRAND_COLOR,
            fontWeight: 500,
            fontSize: "0.8125rem",
            lineHeight: 1.2,
            textDecoration: "none",
            opacity: 0.92,
            transition: "opacity 0.15s ease, color 0.15s ease",
            "&:hover": {
              color: BRAND_HOVER,
              opacity: 1
            }
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16, flexShrink: 0 }} />
          {t("back", "Back")}
        </Box>

        <Box
          sx={{
            display: { xs: "grid", md: "flex" },
            gridTemplateColumns: "minmax(0, 1fr)",
            minWidth: 0,
            width: { xs: "100%", md: "100%" },
            maxWidth: {
              xs: "min(100%, 280px)",
              sm: "min(100%, 280px)",
              md: "none"
            },
            mx: { xs: "auto", md: 0 },
            gap: { xs: 1, sm: 1.25, md: 2 },
            justifyContent: { md: "flex-end" },
            alignItems: { md: "center" },
            flexWrap: { md: "wrap" },
            justifyItems: "stretch",
            "& > *": {
              width: { xs: "100%", md: "auto" },
              minHeight: { xs: 36, md: "auto" }
            }
          }}
        >
          <PrintButton
            size="small"
            targetRef={printContentRef}
            title={`${t("receipt-detail-print-title", "Receipt")}: ${receipt.receiptId}`}
            label={t("receipt-detail-print-label", "Print Receipt")}
          />
        </Box>
      </Box>

      <PrintableContent id="receipt-printable-content" ref={printContentRef}>
        <ReceiptDetailPrintableBody
          receipt={receipt}
          invoiceDetail={invoiceDetail}
          invoiceLinked={linkedInvoice}
          orderForPrint={orderForPrint}
          invoiceDetailPath={invoiceDetailPath}
          paymentDetailPath={paymentDetailPath}
          salesOrderDetailPath={salesOrderDetailPath}
          t={t}
        />
      </PrintableContent>
    </Box>
  );
};

export default ReceiptDetail;
